/**
 * 主动气泡：静音、券到期、浏览沉默、隐藏
 */

import { Toast } from 'vant';
import globalConfig from '@utiles/officialStore/globalConfig';
import { mountVueTip } from '@components/officialTopupNew/payAssistant/agent-tips/mountVueTip';
import {
  loadBrowseSilenceTip,
  loadCouponExpireTip,
  loadPayFailTip,
  loadPaySuccessTip,
} from '@components/officialTopupNew/payAssistant/agent-tips/loadAgentTip';
import { resolvePayFailTipConfig } from '@configs/officialTopupNew/payFailTip';
import {
  ensurePayAssistant,
  getAgentTipProps,
  getEntryTexts,
  safeWithChat,
} from './instance';
import { trackPayAssistantHostAction } from './analytics';
import {
  trackCouponBubbleShow,
  trackCouponQueryClick,
} from './couponTip';
import {
  handlePayFailTipAction,
  loadPayFailTipForOrder,
  trackPayFailTipClick,
  trackPayFailTipShow,
} from './payFailTip';

const TIP_MUTE_KEY_PREFIX = 'agent_tip_mute_until';
const TIP_MUTE_MS = 24 * 60 * 60 * 1000;
/** 主动气泡无操作自动收回：5 秒 */
const TIP_AUTO_HIDE_MS = 5 * 1000;

let tipAutoHideTimer = null;

function getAgentTipMuteKey(gameId) {
  const id = gameId || globalConfig.getHrefParam('gameId') || 'unknown';
  return `${TIP_MUTE_KEY_PREFIX}_${id}`;
}

export function isAgentTipMuted(gameId) {
  try {
    const until = Number(localStorage.getItem(getAgentTipMuteKey(gameId)) || 0);
    return until > Date.now();
  } catch (e) {
    return false;
  }
}

export function muteAgentTips24h(gameId) {
  try {
    localStorage.setItem(
      getAgentTipMuteKey(gameId),
      String(Date.now() + TIP_MUTE_MS),
    );
  } catch (e) {
    // 隐私模式等写失败可忽略
  }
}

function clearTipAutoHide() {
  if (tipAutoHideTimer) {
    clearTimeout(tipAutoHideTimer);
    tipAutoHideTimer = null;
  }
}

function scheduleTipAutoHide(chat, tipId, onAutoHide) {
  clearTipAutoHide();
  tipAutoHideTimer = setTimeout(() => {
    tipAutoHideTimer = null;
    chat.hideTip(tipId);
    if (typeof onAutoHide === 'function') onAutoHide();
  }, TIP_AUTO_HIDE_MS);
}

/**
 * 优惠券到期主动气泡
 * @param {Object} options
 * @param {Object} options.coupon
 * @param {string} [options.tipId]
 * @param {Function} [options.onViewDetail]
 * @param {Function} [options.onMute]
 * @param {Function} [options.onClose]
 * @param {Function} [options.onAutoHide]
 */
export async function showCouponExpireTip(options = {}) {
  try {
    const chat = await ensurePayAssistant();
    const CouponExpireTip = await loadCouponExpireTip();
    const tipId = options.tipId || 'anti_harass_coupon_expire';
    const coupon = options.coupon || {};
    const texts = getEntryTexts();
    const muteToast = texts.muteToast || '';

    clearTipAutoHide();
    trackPayAssistantHostAction('tip_show', {
      category: 'tip',
      tipKind: 'coupon_expire',
      tipId,
      couponId: coupon.COUPON_ID,
    });
    trackCouponBubbleShow(coupon);
    chat.showTip({
      tipId,
      duration: 0,
      render(el) {
        return mountVueTip(
          el,
          CouponExpireTip,
          {
            coupon,
            ...getAgentTipProps(),
            message: texts.couponExpireMessage || '',
            muteBtn: texts.muteBtn || '',
            viewDetailBtn: texts.couponExpireViewDetailBtn || '',
          },
          {
            async viewDetail() {
              clearTipAutoHide();
              trackPayAssistantHostAction('tip_got_it_btn', {
                category: 'tip',
                tipKind: 'coupon_expire',
                tipId,
              });
              trackCouponQueryClick({
                tipId,
                couponId: coupon?.COUPON_ID || '',
              });
              if (typeof options.onViewDetail === 'function') {
                options.onViewDetail();
              }
              if (await chat.isAvailable()) {
                await chat.show(
                  {
                    inputValue: '',
                    extensions: { skill: 'coupon_detail', coupon },
                  },
                  'dialog',
                );
              }
            },
            mute() {
              clearTipAutoHide();
              trackPayAssistantHostAction('tip_quiet_btn', {
                category: 'tip',
                tipKind: 'coupon_expire',
                tipId,
              });
              muteAgentTips24h();
              chat.hideTip(tipId);
              if (muteToast) Toast(muteToast);
              if (typeof options.onMute === 'function') options.onMute();
            },
            close() {
              clearTipAutoHide();
              trackPayAssistantHostAction('tip_hide', {
                category: 'tip',
                tipKind: 'coupon_expire',
                tipId,
                reason: 'close',
              });
              chat.hideTip(tipId);
              if (typeof options.onClose === 'function') options.onClose();
            },
          },
        );
      },
    });
    scheduleTipAutoHide(chat, tipId, options.onAutoHide);
    return true;
  } catch (err) {
    console.warn('[PayAssistant] showCouponExpireTip failed', err);
    return false;
  }
}

/**
 * 浏览沉默问询主动气泡
 */
export async function showBrowseSilenceTip(options = {}) {
  try {
    const chat = await ensurePayAssistant();
    const BrowseSilenceTip = await loadBrowseSilenceTip();
    const tipId = options.tipId || 'anti_harass_browse_silence';
    const texts = getEntryTexts();
    const muteToast = texts.muteToast || '';

    clearTipAutoHide();
    trackPayAssistantHostAction('tip_show', {
      category: 'tip',
      tipKind: 'browse_silence',
      tipId,
    });
    chat.showTip({
      tipId,
      duration: 0,
      render(el) {
        return mountVueTip(
          el,
          BrowseSilenceTip,
          {
            ...getAgentTipProps(),
            message: texts.browseSilenceMessage || '',
            muteBtn: texts.browseSilenceMuteBtn || '',
            viewDetailBtn: texts.browseSilenceViewDetailBtn || '',
          },
          {
            async viewDetail() {
              trackPayAssistantHostAction('tip_got_it_btn', {
                category: 'tip',
                tipKind: 'browse_silence',
                tipId,
              });
              if (typeof options.onViewDetail === 'function') {
                options.onViewDetail();
              }
              if (await chat.isAvailable()) {
                await chat.show(
                  {
                    inputValue: '',
                    extensions: { skill: 'need_help_ask' },
                  },
                  'dialog',
                );
              }
            },
            mute() {
              trackPayAssistantHostAction('tip_quiet_btn', {
                category: 'tip',
                tipKind: 'browse_silence',
                tipId,
              });
              muteAgentTips24h();
              chat.hideTip(tipId);
              if (muteToast) Toast(muteToast);
              if (typeof options.onMute === 'function') options.onMute();
            },
            close() {
              trackPayAssistantHostAction('tip_hide', {
                category: 'tip',
                tipKind: 'browse_silence',
                tipId,
                reason: 'close',
              });
              chat.hideTip(tipId);
              if (typeof options.onClose === 'function') options.onClose();
            },
          },
        );
      },
    });
    return true;
  } catch (err) {
    console.warn('[PayAssistant] showBrowseSilenceTip failed', err);
    return false;
  }
}

/**
 * 支付结果页失败引导气泡（get_orders 返回 result=-1 + failed_type）
 * @param {{
 *   gameId?: string|number,
 *   orderId?: string,
 *   tipId?: string,
 *   payload?: Awaited<ReturnType<typeof loadPayFailTipForOrder>>,
 *   resumeContext?: import('./payFailResume').PayFailHomeContext,
 * }} options
 * @returns {Promise<boolean>}
 */
export async function showPayFailTip(options = {}) {
  const payload = options.payload;
  if (!payload) return false;

  try {
    const chat = await ensurePayAssistant();
    const PayFailTip = await loadPayFailTip();
    const tipId = options.tipId || 'pay_fail_tip';
    const { order } = payload;
    const resumeContext = options.resumeContext || {};
    // poll 阶段可能尚未 setup，entryTexts 为空；展示前再按当前语言解析文案
    const tip = resolvePayFailTipConfig(
      (payload.tip && payload.tip.failType) || order.failed_type,
      getEntryTexts(),
    );

    trackPayFailTipShow(tip, { ...order, f_pay_type: payload?.f_pay_type || '', pay_type: payload?.pay_type || '' });
    chat.showTip({
      tipId,
      duration: 0,
      render(el) {
        return mountVueTip(
          el,
          PayFailTip,
          {
            ...getAgentTipProps(),
            message: tip.message,
            primaryLabel: (tip.primary && tip.primary.label) || '',
            secondaryLabel: (tip.secondary && tip.secondary.label) || '',
          },
          {
            async primary() {
              if (!tip.primary) return;
              trackPayFailTipClick('primary', tip, order);
              chat.hideTip(tipId);
              await handlePayFailTipAction(tip.primary.action, resumeContext);
            },
            async secondary() {
              if (!tip.secondary) return;
              trackPayFailTipClick('secondary', tip, order);
              chat.hideTip(tipId);
              await handlePayFailTipAction(tip.secondary.action, resumeContext);
            },
            close() {
              trackPayAssistantHostAction('tip_hide', {
                category: 'tip',
                tipKind: 'pay_fail',
                tipId,
                failType: tip.failType,
                reason: 'close',
              });
              chat.hideTip(tipId);
            },
          },
        );
      },
    });
    return true;
  } catch (err) {
    console.warn('[PayAssistant] showPayFailTip failed', err);
    return false;
  }
}

/**
 * 支付结果页成功/到账中引导气泡（无按钮）
 * @param {{ tipId?: string, message?: string, onClose?: Function, onAutoHide?: Function }} [options]
 * @returns {Promise<boolean>}
 */
export async function showPaySuccessTip(options = {}) {
  try {
    const chat = await ensurePayAssistant();
    const PaySuccessTip = await loadPaySuccessTip();
    const tipId = options.tipId || 'pay_success_tip';
    const texts = getEntryTexts();
    const message =
      options.message ||
      texts.paySuccessMessage ||
      '';

    clearTipAutoHide();
    trackPayAssistantHostAction('tip_show', {
      category: 'tip',
      tipKind: 'pay_success',
      tipId,
    });
    chat.showTip({
      tipId,
      duration: 0,
      render(el) {
        return mountVueTip(
          el,
          PaySuccessTip,
          {
            ...getAgentTipProps(),
            message,
          },
          {
            close() {
              clearTipAutoHide();
              trackPayAssistantHostAction('tip_hide', {
                category: 'tip',
                tipKind: 'pay_success',
                tipId,
                reason: 'close',
              });
              chat.hideTip(tipId);
              if (typeof options.onClose === 'function') options.onClose();
            },
          },
        );
      },
    });
    scheduleTipAutoHide(chat, tipId, options.onAutoHide);
    return true;
  } catch (err) {
    console.warn('[PayAssistant] showPaySuccessTip failed', err);
    return false;
  }
}

/** 隐藏 tip 并清理自动收回定时器；【兜底】用 safeWithChat 吞错 */
export async function hideAgentTip(tipId) {
  clearTipAutoHide();
  await safeWithChat((chat) => {
    if (tipId) chat.hideTip(tipId);
    else chat.hideTip();
  });
}

/** 供 destroy 时清理 tip 定时器 */
export function clearAgentTipTimers() {
  clearTipAutoHide();
}
